import { format } from "~/tools/date-time";

import type { BorgContextPin } from "../models/borg-types";

const MAX_LABEL_LENGTH = 40;

const truncate = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/** Short display label for a context pin chip. Long values are truncated. */
export const formatPinLabel = (pin: BorgContextPin): string => {
	switch (pin.type) {
		case "component":
			return pin.name ?? "Component";
		case "topology_time":
			if (pin.point_ms != null) {
				return `Topology: ${format(pin.point_ms, "HH:mm:ss")}`;
			}
			return "Topology: Live";
		case "telemetry_interval": {
			const label = pin.range_label ?? "Telemetry interval";
			return pin.markers_ms?.length ? `${label} + ${pin.markers_ms.length} markers` : label;
		}
		case "metric_time_range":
			return `Chart: ${pin.range_label ?? "Custom range"}`;
		case "time_range":
			return pin.point_ms != null ? `Time: ${format(pin.point_ms, "HH:mm:ss")}` : "Time range";
		case "filter":
			return pin.value ? truncate(`${pin.name}: ${pin.value}`, MAX_LABEL_LENGTH) : (pin.name ?? "Filter");
		case "query":
			return truncate(pin.query ?? "Query", MAX_LABEL_LENGTH);
	}
};

const formatTimeRangeTooltip = (start: number | undefined, end: number | undefined, label: string): string => {
	if (start == null) {
		return label;
	}
	const endStr = end != null ? format(end, "MMM d, yyyy, HH:mm:ss") : "now";
	const prefix = label ? `${label}: ` : "";
	return `${prefix}${format(start, "MMM d, yyyy, HH:mm:ss")} → ${endStr}`;
};

const tooltipComponent = (pin: Extract<BorgContextPin, { type: "component" }>): string => {
	const parts = [pin.name ?? "Component"];
	if (pin.component_type) {
		parts.push(`Type: ${pin.component_type}`);
	}
	if (pin.health_state) {
		parts.push(`Health: ${pin.health_state}`);
	}
	if (pin.urn) {
		parts.push(`URN: ${pin.urn}`);
	}
	return parts.join("\n");
};

const tooltipTelemetryInterval = (pin: Extract<BorgContextPin, { type: "telemetry_interval" }>): string => {
	const parts: Array<string> = [];
	const label = pin.range_label ? `Telemetry interval (${pin.range_label})` : "Telemetry interval";
	parts.push(formatTimeRangeTooltip(pin.start_ms, pin.end_ms, label));
	if (pin.markers_ms != null && pin.markers_ms.length > 0) {
		parts.push(`Markers: ${pin.markers_ms.map((ms) => format(ms, "HH:mm:ss")).join(", ")}`);
	}
	return parts.join("\n");
};

/** Full untruncated tooltip text showing all pin details. */
export const formatPinTooltip = (pin: BorgContextPin): string => {
	switch (pin.type) {
		case "component":
			return tooltipComponent(pin);
		case "topology_time":
			if (pin.point_ms != null) {
				return `Topology time: time-traveling to ${format(pin.point_ms, "MMM d, yyyy, HH:mm:ss")}\nComponents and health states shown as they existed at this moment.`;
			}
			return "Topology time: Live\nShowing current component state.";
		case "telemetry_interval":
			return tooltipTelemetryInterval(pin);
		case "metric_time_range":
			return `Maximized chart range\n${formatTimeRangeTooltip(pin.start_ms, pin.end_ms, pin.range_label ?? "")}\nThis chart has been zoomed/brushed to a different range than the global telemetry interval.`;
		case "time_range":
			return pin.point_ms != null ? `Time travel: ${format(pin.point_ms, "MMM d, yyyy, HH:mm:ss")}` : "Time range";
		case "filter":
			return pin.value ? `${pin.name}: ${pin.value}` : (pin.name ?? "Filter");
		case "query":
			return pin.query ?? "Query";
	}
};
